import React from 'react';
import PropTypes from 'prop-types';
import BlackTick from '../Icons/BlackTick';
import {
  CardContainer,
  CardHeader,
  CardTitle,
  CardPrice,
  CardDescription,
  FeatureList,
  FeatureItem,
  ActionButton,
  HighlightBox,
  PriceText,
  ScreenReaderOnly,
  FactSheet,
} from './index.styles';
import { products, benefitsDocument } from '../../constants';
import { ExternalLink } from '../Icons/ExternalLink';

const ProductCard = ({
  productConfig,
  buttonVariant = 'primary',
  href,
  onButtonClick,
  isPaidSubscriptionCard,
  previousSubscriptionFamily = null,
}) => {
  const { title, price, frequency, description, features, buttonText, priceText, highlight } = productConfig;

  const IdentityFactSheetLink = `${benefitsDocument.baseUrl}/${benefitsDocument[products.identity]}`;

  const CreditExpertFactSheetLink = `${benefitsDocument.baseUrl}/${benefitsDocument[products.credit_expert]}`;

  const factSheetLinks = {
    'Identity Plus': IdentityFactSheetLink,
    CreditExpert: CreditExpertFactSheetLink,
  };

  const factSheetHref = factSheetLinks[previousSubscriptionFamily];

  return (
    <CardContainer>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {frequency === null || frequency === undefined ? (
          <React.Fragment>
            <ScreenReaderOnly>This account is free of charge</ScreenReaderOnly>
            <CardPrice isBold={false} aria-hidden="true">
              {price}
            </CardPrice>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <CardPrice isBold>{price} /</CardPrice>
            <CardPrice isBold={false}> {frequency}</CardPrice>
          </React.Fragment>
        )}
      </CardHeader>

      {highlight && <HighlightBox>{highlight}</HighlightBox>}

      {description && <CardDescription>{description}</CardDescription>}

      <FeatureList variant={buttonVariant} aria-label="Benefits Include">
        {features.map(feature => (
          <FeatureItem key={feature} isPaidSubscriptionCard={isPaidSubscriptionCard}>
            <BlackTick />
            {feature}
          </FeatureItem>
        ))}
        {previousSubscriptionFamily && factSheetHref && (
          <FactSheet>
            <a href={factSheetHref} target="_blank" rel="noopener noreferrer">
              See all {previousSubscriptionFamily} benefits <ExternalLink chooser />
            </a>
          </FactSheet>
        )}
        {priceText && <PriceText>{priceText}</PriceText>}
      </FeatureList>

      {href ? (
        <LinkButton variant={buttonVariant} href={href} onClick={onButtonClick}>
          {buttonText}
        </LinkButton>
      ) : (
        <ActionButton variant={buttonVariant} onClick={onButtonClick}>
          {buttonText}
        </ActionButton>
      )}
    </CardContainer>
  );
};

const LinkButton = ({ href, children, variant, onClick }) => {
  const handleClick = e => {
    if (onClick) {
      onClick(e);
    }

    if (href) {
      window.location.href = href;
    }
  };

  return (
    <ActionButton variant={variant} onClick={handleClick}>
      {children}
    </ActionButton>
  );
};

LinkButton.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['primary', 'secondary']),
  onClick: PropTypes.func,
};

ProductCard.propTypes = {
  productConfig: PropTypes.shape({
    title: PropTypes.string.isRequired,
    price: PropTypes.string.isRequired,
    frequency: PropTypes.string,
    description: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.string).isRequired,
    buttonText: PropTypes.string.isRequired,
    highlight: PropTypes.string,
    priceText: PropTypes.string,
  }).isRequired,
  buttonVariant: PropTypes.oneOf(['primary', 'secondary']),
  href: PropTypes.string,
  onButtonClick: PropTypes.func,
  isPaidSubscriptionCard: PropTypes.bool,
  previousSubscriptionFamily: PropTypes.string,
};

ProductCard.defaultProps = {
  buttonVariant: 'primary',
  onButtonClick: () => {},
  isPaidSubscriptionCard: false,
};

export default ProductCard;
